/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { PageSEO } from '@/shared/seo';
import {
  useDataStateOverride,
  applyDataStateOverride,
} from '@/shared/ui/components/basic/data-state/use-data-state-override';
import { AgendaToolbar } from '@features/agenda/components/AgendaToolbar';
import { AgendaSidebar } from '@features/agenda/components/AgendaSidebar';
import { AgendaMobileView } from '@features/agenda/components/AgendaMobileView';
import { AgendaEventList } from '@features/agenda/components/AgendaEventList';
import { agendaLabel } from './agenda-label';
import { AgendaStates } from './AgendaStates';
import { useAgenda } from './AgendaPage.useAgenda';
import { AgendaCalendarView } from './AgendaPage.calendarView';
import { AgendaOverlays } from './AgendaPage.overlays';
import { AgendaDesktopSkeleton } from './AgendaPage.desktopSkeleton';
import { AgendaMobileSkeleton } from './AgendaPage.mobileSkeleton';

/**
 * Unified agenda page: day/week/month planner with event and calendar CRUD.
 *
 * Sidebar grouping:
 *   operational+: Padrão / Minhas agendas / Agendas dos membros (lazy per-member)
 *   broker: Padrão / Minhas agendas
 *
 * @returns Rendered agenda page.
 */
const AgendaPage = () => {
  const a = useAgenda();
  const override = useDataStateOverride();

  const monthLabel = a.t(`month_long.${a.anchor.getMonth()}`);
  const rawLoading =
    !a.agencyId || a.eventsQuery.isPending || a.calendarsQuery.isPending;
  const { isLoading: agendaLoading } = applyDataStateOverride(override, {
    isLoading: rawLoading,
    isEmpty: a.visibleEvents.length === 0,
  });
  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--surface-secondary)] md:p-(--space-5)">
      <PageSEO title={a.t('page_title')} />
      {a.isMobile ? (
        agendaLoading ? (
          <AgendaMobileSkeleton />
        ) : (
        <AgendaMobileView
          view={a.view}
          anchor={a.anchor}
          monthLabel={monthLabel}
          events={a.visibleEvents}
          calendars={a.calendars}
          canCreateEvents={a.canCreateEvents}
          currentUserRole={a.currentUserRole}
          userEmail={a.userEmail ?? ''}
          onViewChange={a.setView}
          onNavigate={a.navigate}
          onSelectDate={a.goToDay}
          onSelectEvent={a.openEvent}
          onSlotCreate={a.openCreate}
          onAdd={() => a.openCreate()}
          onEditFromPreview={a.editFromPreview}
          onDeleteFromPreview={a.requestDelete}
          onOpenSettings={() => a.setDrawerOpen(true)}
          activeCalendars={a.activeCalendars}
          onToggleCalendar={a.toggleCalendar}
          visitsVisible={a.visitsVisible}
          onToggleVisits={a.toggleVisits}
          agencyName={a.agency.name}
        />
        )
      ) : agendaLoading ? (
        <AgendaDesktopSkeleton />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border-subtle)] shadow-[var(--shadow-sm)]">
        {/* Toolbar bar */}
        <div className="shrink-0 border-b border-[var(--border-subtle)] bg-[var(--surface-primary)] px-(--space-5) py-(--space-3)">
          <AgendaToolbar
            view={a.view}
            label={agendaLabel(
              a.view,
              a.anchor,
              (m) => a.t(`month_short.${m}`),
              (d) => a.t(`weekday.${d}`),
              (m) => a.t(`month_long.${m}`)
            )}
            onViewChange={a.setView}
            onNavigate={a.navigate}
            onToday={a.goToday}
          />
        </div>
        {/* Body: sidebar + content */}
        <div className="flex min-h-0 flex-1">
          <AgendaSidebar
            calendars={a.myCalendars}
            allCalendars={a.calendars}
            activeCalendars={a.activeCalendars}
            onToggleCalendar={a.toggleCalendar}
            anchor={a.anchor}
            onSelectDate={a.goToDay}
            agencyName={a.agency.name}
            members={a.nonMeMembers}
            activeMembers={a.activeMembers}
            onToggleMember={a.toggleMember}
            canViewMemberAgendas={a.isOperational && a.agency.type !== 'solo_broker'}
            currentUserEmail={a.userEmail ?? ''}
            onCreate={() => a.openCreate()}
            canCreate={a.canCreateEvents}
            onManageCalendars={() => a.setDrawerOpen(true)}
            visitsVisible={a.visitsVisible}
            onToggleVisits={a.toggleVisits}
          />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto p-(--space-3)">
            <AgendaStates
              isLoading={a.eventsQuery.isLoading}
              isError={a.eventsQuery.isError}
              isEmpty={a.visibleEvents.length === 0}
              onRetry={() => a.eventsQuery.refetch()}
            >
              {a.view === 'list' ? (
                <AgendaEventList
                  anchor={a.anchor}
                  events={a.visibleEvents}
                  calendars={a.calendars}
                  onSelect={a.openEvent}
                />
              ) : (
                <AgendaCalendarView
                  view={a.view}
                  anchor={a.anchor}
                  events={a.visibleEvents}
                  calendars={a.calendars}
                  canCreateEvents={a.canCreateEvents}
                  onSelect={a.openEvent}
                  onSlotCreate={a.openCreate}
                  onDayClick={a.goToDay}
                />
              )}
            </AgendaStates>
          </div>
        </div>
      </div>
      )}

      <AgendaOverlays
        agencyId={a.agencyId}
        agencyName={a.agency.name}
        userEmail={a.userEmail ?? ''}
        userRole={a.currentUserRole}
        calendars={a.calendars}
        calendarsLoading={a.calendarsQuery.isLoading}
        selected={a.selected}
        canManageSelected={a.canManageSelected}
        previewOpen={a.previewOpen}
        formOpen={a.formOpen}
        drawerOpen={a.drawerOpen}
        formDefaultDate={a.formDefaultDate}
        pendingEdit={a.pendingEdit}
        pendingDelete={a.pendingDelete}
        onClosePreview={() => a.setPreviewOpen(false)}
        onEditFromPreview={a.editFromPreview}
        onRequestDelete={a.requestDelete}
        onViewVisit={a.viewVisit}
        onCloseForm={() => a.setFormOpen(false)}
        onCreateEvent={a.handleCreateEvent}
        onUpdateEvent={a.handleUpdateEvent}
        onConfirmEditScope={a.confirmEditScope}
        onConfirmDeleteScope={a.confirmDeleteScope}
        onClearPendingEdit={() => a.setPendingEdit(null)}
        onClearPendingDelete={() => a.setPendingDelete(null)}
        onCreateCalendar={(input) => a.createCalendar.mutate(input)}
        onUpdateCalendar={(calendarId, body) => a.updateCalendar.mutate({ calendarId, body })}
        isSavingCalendar={a.updateCalendar.isPending}
        onDeleteCalendar={(id) => a.deleteCalendar.mutate(id)}
        onCloseDrawer={() => a.setDrawerOpen(false)}
      />
    </div>
  );
};

export default AgendaPage;
